"use client";
import React, { useState } from "react";
import { Eye, EyeOff, Mail, Lock, AlertCircle } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    // Client-side validation
    const newFieldErrors: { email?: string; password?: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      newFieldErrors.email = "Please enter your email address";
    } else if (!emailRegex.test(email)) {
      newFieldErrors.email = "Invalid Email format";
    }

    if (!password) {
      newFieldErrors.password = "Please enter your password";
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    setLoading(true);

    try {
      await login(email, password, remember);
      // Navigation handled by AuthContext
    } catch (err: any) {
      setError(err.response?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 py-8 font-sans">
      <div className="w-full max-w-[400px] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-gray-100 rounded-xl p-6 sm:p-8 transition-all hover:border-[#885ABB]/20">
        {/* Branch-specific header */}
        <div className="flex flex-col items-center text-center w-full border-[#885ABB] mb-6 sm:mb-8">
          <img src="/glossa-purple.png" alt="Glossa" className="h-6 sm:h-7 w-auto mb-3 sm:mb-4" />
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">Sign In</h1>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">Access your business management portal</p>
        </div>

        <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit} noValidate>
          <div className="space-y-3">
            {/* Email Field */}
            <div className="space-y-1">
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: "" });
                  }}
                  className={`w-full h-[46px] sm:h-[48px] pl-11 pr-4 bg-gray-50/50 border ${fieldErrors.email ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"
                    } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                />
              </div>
              {fieldErrors.email && (
                <p className="text-[11px] text-red-500 font-medium ml-1 flex items-center gap-1 animate-in fade-in slide-in-from-top-1">
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-1">
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: "" });
                  }}
                  className={`w-full h-[46px] sm:h-[48px] pl-11 pr-11 bg-gray-50/50 border ${fieldErrors.password ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"
                    } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-[11px] text-red-500 font-medium ml-1 flex items-center gap-1 animate-in fade-in slide-in-from-top-1">
                  {fieldErrors.password}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 text-[13px]">
            <label className="flex items-center text-gray-500 cursor-pointer group hover:text-gray-700 transition-colors">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-3.5 w-3.5 text-[#885ABB] border-gray-300 rounded focus:ring-[#885ABB] transition-all"
              />
              <span className="ml-2 font-normal">Remember me</span>
            </label>

            <a href="/forgot-password" title="Recover password access" className="text-blue-500  hover:text-blue-600 hover:underline text-xs sm:text-[13px] transition-colors">
              Forgot password?
            </a>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 text-red-600 bg-red-50 text-[12px] rounded-lg border border-red-100 animate-pulse">

              <p className="font-medium">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#885ABB] to-[#7647a8] text-white h-[46px] sm:h-[48px] rounded-lg font-semibold text-sm shadow-md shadow-[#885ABB]/10 hover:shadow-lg hover:opacity-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed = tracking-widest"
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div className="relative my-6 sm:my-7">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-100"></span></div>
          <div className="relative flex justify-center text-[10px] uppercase"><span className="bg-white px-3 text-gray-300 font-semibold tracking-[0.15em]">OR</span></div>
        </div>

        <p className="text-center text-[13px] text-gray-400 font-medium">
          Don't have an account?{" "}
          <a href="/signup" className="text-blue-500 hover:text-blue-600 hover:underline ml-1 transition-colors">
            Sign up
          </a>
        </p>
      </div>
    </div>
  );
}
